import React, { ReactNode, useContext, useRef, useState } from 'react'

import * as ReactRadioGroup from '@radix-ui/react-radio-group'
import { Flex } from 'antd'
import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'
import { v4 as uuid } from 'uuid'

import FormControl from '../FormControl/FormControl'
import { FormControlProps } from '../FormControl/util'
import Icon from '../Icon/Icon'
import { IconKey } from '../Icon/util'
import { T } from '../Text/Text'

export type RadioGroupVariant = 'default' | 'tiles'

type RadioGroupBaseProps<TValue extends string> = FormControlProps & {
  children: ReactNode
  onValueChange: (value: TValue) => void
  value: TValue | undefined
  variant?: RadioGroupVariant
}
const RadioGroupBase = <TValue extends string>({
  ariaLabel,
  children,
  errorMessage,
  label,
  onValueChange,
  value,
  variant = 'default',
  id: idProp,
  isInvalid,
  isRequired,
}: RadioGroupBaseProps<TValue>) => {
  const [id] = useState(idProp ?? uuid())
  return (
    <RadioGroupContext.Provider value={{ variant, selectedValue: value }}>
      <FormControl
        errorMessage={errorMessage}
        id={id}
        label={label}
        isInvalid={isInvalid}
        isRequired={isRequired}
        width="100%"
      >
        {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
          <ReactRadioGroup.Root
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-label={ariaLabel}
            aria-live={ariaLive}
            value={value}
            onValueChange={onValueChange}
            className="w-full"
          >
            <Flex wrap="wrap" gap="6px">
              {children}
            </Flex>
          </ReactRadioGroup.Root>
        )}
      </FormControl>
    </RadioGroupContext.Provider>
  )
}

const StyledRadioGroupItemContainer = styled(Flex).attrs({
  align: 'center',
  justify: 'start',
  gap: '8px',
  wrap: 'nowrap',
})<{
  $isSelected: boolean
  $variant: RadioGroupVariant
}>`
  ${({ $variant }) =>
    $variant !== 'default' &&
    `
    flex: 1;
  `}
`
const StyledRadioGroupItem = styled(ReactRadioGroup.Item)<{
  $isSelected: boolean
  $variant: RadioGroupVariant
}>`
  display: block;
  cursor: pointer;
  forced-color-adjust: none;

  ${({ $isSelected, $variant, theme }) =>
    $variant === 'default' &&
    `
    padding: 0px;
    background-color: white;
    min-width: 16px;
    min-height: 16px;
    border-radius: 100%;
    border: solid 1px ${$isSelected ? theme.colorPrimaryBase : theme.colorBorder};
    &:hover {
      border-color: ${theme.colorPrimaryBase};
    }
  `}
`
const StyledRadioGroupItemIndicator = styled(ReactRadioGroup.Indicator)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 10px;
  min-height: 10px;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: ${({ theme }) => theme.colorPrimaryBase};
  }
`

type RadioGroupItemProps<TValue extends string> = {
  description?: string
  icon?: IconKey
  isDisabled?: boolean
  label: ReactNode
  value: TValue
}
const RadioGroupItem = <TValue extends string>({
  description,
  icon,
  isDisabled,
  label,
  value,
}: RadioGroupItemProps<TValue>) => {
  const [id] = useState(uuid())
  const { variant, selectedValue } = useRadioGroupContext<TValue>()
  const isSelected = selectedValue === value
  const radioInputRef = useRef<HTMLButtonElement>(null)

  const labelNode = (
    <label htmlFor={id} className={isDisabled ? 'cursor-not-allowed' : ''}>
      {typeof label === 'string' ? <T.Body className="text-left">{label}</T.Body> : label}
    </label>
  )

  if (variant === 'tiles') {
    return (
      <ReactRadioGroup.Item
        className={twMerge(
          'flex flex-1 items-start rounded-lg border border-neutral-200 px-4 py-3 outline-offset-2 outline-sky-800 focus-visible:outline',
          isSelected ? 'border-orange-600 bg-neutral-50' : 'hover:bg-neutral-50',
          isDisabled ? 'bg-neutral-150 hover:bg-neutral-150 cursor-not-allowed' : ''
        )}
        disabled={isDisabled}
        id={id}
        ref={radioInputRef}
        value={value}
      >
        <div
          className={twMerge(
            'grid items-start gap-x-3 gap-y-1',
            icon ? 'grid-cols-[auto_1fr]' : 'grid-cols-1'
          )}
        >
          {icon && (
            <T.Body
              className={twMerge('flex h-[20px] items-center', description ? 'row-span-2' : '')}
            >
              <Icon icon={icon} className="text-neutral-500" />
            </T.Body>
          )}
          {labelNode}
          {description && (
            <T.BodySmall className="text-left text-neutral-600">{description}</T.BodySmall>
          )}
        </div>
      </ReactRadioGroup.Item>
    )
  }

  return (
    <StyledRadioGroupItemContainer $isSelected={isSelected} $variant={variant}>
      <StyledRadioGroupItem
        $isSelected={isSelected}
        $variant={variant}
        disabled={isDisabled}
        id={id}
        ref={radioInputRef}
        value={value}
        className="outline-offset-2 outline-sky-800 focus-visible:outline"
      >
        {variant === 'default' && <StyledRadioGroupItemIndicator />}
      </StyledRadioGroupItem>
      {labelNode}
    </StyledRadioGroupItemContainer>
  )
}

type RadioGroupContextType<TValue> = {
  variant: RadioGroupVariant
  selectedValue: TValue
}
const RadioGroupContext = React.createContext<RadioGroupContextType<any>>(
  {} as RadioGroupContextType<any>
)
const useRadioGroupContext = <TValue extends string>() =>
  useContext<RadioGroupContextType<TValue>>(RadioGroupContext)

export default Object.assign(RadioGroupBase, {
  Item: RadioGroupItem,
})
